import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import SummaryCard from "../SummaryCard";
import TrainerCostsTable from "./TrainerCostsTable";
import LocationCostsTable from "./LocationCostsTable";
import ConstantCostsBreakdown from "./ConstantCostsBreakdown";
import { formatCurrency, profitBg } from "../../utils/formatters";
import { GRADIENTS } from "../../utils/gradients";

const CostsStatistics = ({ costData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!costData) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        Brak danych do wyświetlenia
      </Typography>
    );
  }

  return (
    <>
      {costData.warnings && costData.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Uwagi dotyczące kosztów:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {costData.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<MoneyIcon />}
            label="Całkowity przychód"
            value={formatCurrency(costData.total_revenue)}
            gradient={GRADIENTS.purple}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<AccountBalanceIcon />}
            label="Całkowite koszty"
            value={formatCurrency(costData.total_costs)}
            caption={`${costData.total_lessons_count} zajęć`}
            gradient={GRADIENTS.pink}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<AccountBalanceIcon />}
            label={`Podatek (${costData.income_tax_rate}%)`}
            value={formatCurrency(costData.income_tax_amount)}
            gradient={GRADIENTS.orange}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<TrendingUpIcon />}
            label="Zysk netto"
            value={formatCurrency(costData.net_profit)}
            gradient={profitBg(costData.net_profit)}
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Szczegółowy podział
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Przychody
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Karnety:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.pass_revenue)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Wejścia jednorazowe:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.single_entry_revenue)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Razem:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{formatCurrency(costData.total_revenue)}</Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Koszty
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Koszty zmienne:
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Trenerzy:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.total_trainer_cost)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Lokalizacje:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.total_location_cost)}</Typography>
              </Box>
              {costData.total_variable_costs !== undefined && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Suma zmiennych:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontStyle: 'italic' }}>{formatCurrency(costData.total_variable_costs)}</Typography>
                </Box>
              )}
              {costData.total_constant_costs !== undefined && costData.total_constant_costs > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Koszty stałe:
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Koszty stałe:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.total_constant_costs)}</Typography>
                  </Box>
                </>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Razem:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>{formatCurrency(costData.total_costs)}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Kalkulacja zysku
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Przychód:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.total_revenue)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">- Koszty:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.total_costs)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Zysk przed opodatkowaniem:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(costData.profit_before_tax)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">- Podatek dochodowy ({costData.income_tax_rate}%):</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(costData.income_tax_amount)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>Zysk netto:</Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, color: costData.net_profit >= 0 ? 'success.main' : 'error.main' }}
            >
              {formatCurrency(costData.net_profit)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <TrainerCostsTable trainerCosts={costData.trainer_costs} isMobile={isMobile} />
      <LocationCostsTable locationCosts={costData.location_costs} isMobile={isMobile} />

      {costData.constant_costs_breakdown && costData.total_constant_costs > 0 && (
        <ConstantCostsBreakdown
          breakdown={costData.constant_costs_breakdown}
          totalConstantCosts={costData.total_constant_costs}
          isMobile={isMobile}
        />
      )}
    </>
  );
};

export default CostsStatistics;
